<template>
  <v-dialog
    data-testid="create_rev_modal"
    data-tourid="create_rev_modal"
    scrollable
    :max-width="currentStep === STEPS.SELECTION ? 650 : 856"
    :persistent="currentStep === STEPS.FORM"
    :value="show"
    @click:outside="currentStep === STEPS.SELECTION ? handleClose() : null"
    @keydown.esc="currentStep === STEPS.SELECTION ? handleClose() : null"
  >
    <TypeSelectionStep
      v-if="currentStep === STEPS.SELECTION"
      data-testid="create_rev_selection"
      data-tourid="create_rev_selection"
      :typeCards="typeCards"
      :hasLimitedCards="hasLimitedCards"
      @selected="handleTypeSelection"
      @close="handleClose"
    />
    <FormStep
      v-if="currentStep === STEPS.FORM"
      :challengeId="challengeId"
      :type="selectedType"
      :replicateRevData="replicateRevData"
      @created="handleClose"
      @loading="isLoading = $event"
      @cancel="handleCancel"
      @visitSuggestion="handleClose"
    />
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import challengePermissions from '@/mixins/challengePermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import TypeSelectionStep from './TypeSelectionStep.vue'
import FormStep from './FormStep.vue'

const STEPS = {
  SELECTION: 'selection',
  FORM: 'form',
}

export default {
  name: 'CreateContentModal',

  mixins: [challengePermissions, trackerMixin],

  data() {
    return {
      currentStep: STEPS.SELECTION,
      selectedType: null,
      isLoading: false,
      STEPS,
    }
  },

  components: {
    TypeSelectionStep,
    FormStep,
  },

  props: {
    show: {
      type: Boolean,
      default() {
        return false
      },
    },

    replicateRevData: {
      type: Object,
      required: false,
      default() {
        return null
      },
    },

    challengeId: {
      type: String,
      required: false,
    },
  },

  mounted() {
    if (this.replicateRevData) {
      this.selectedType = this.replicateRevData.type
      this.currentStep = STEPS.FORM
    }
  },

  computed: {
    ...mapGetters(['organization']),

    isTasksEnabled() {
      return this.organization.featuresEnabled__tasks
    },

    hasLimitedCards() {
      return Boolean(this.typeCards.length < 5)
    },

    shouldShowChallengeCard() {
      return this.canCreateChallenges()
    },

    typeCards() {
      const options = []

      if (this.isTasksEnabled) {
        options.push({
          type: 'task',
          title: this.$t('tasks.task_one'),
          icon: 'mdi-check',
          description: this.$t('tasks.create_description'),
        })
      }

      if (this.organization.featuresEnabled__quickAction) {
        options.push({
          type: 'quickAction',
          title:
            this.organization?.customNamings['quickAction']?.singular ||
            this.$t('common.rev_type.quick_action_one'),
          icon: 'mdi-lightning-bolt-outline',
          description: this.$t('rev.create.quick_action_card_description'),
        })
      }

      if (this.organization.featuresEnabled__improvementIdea) {
        options.push({
          type: 'improvementIdea',
          title:
            this.organization?.customNamings['improvementIdea']?.singular ||
            this.$t('common.rev_type.improvement_idea_one'),
          icon: 'mdi-lightbulb-outline',
          description: this.$t('rev.create.improvement_idea_card_description'),
        })
      }

      if (this.organization.featuresEnabled__problemSolving) {
        options.push({
          type: 'problemSolving',
          title:
            this.organization?.customNamings['problemSolving']?.singular ||
            this.$t('common.rev_type.problem_solving_one'),
          icon: 'mdi-puzzle-outline',
          description: this.$t('rev.create.problem_solving_card_description'),
        })
      }

      options.push({
        type: 'recognition',
        title: this.$t('recognitions.recognition_other'),
        icon: 'mdi-medal-outline',
        description: this.$t('recognitions.celebrate_colleagues'),
      })

      if (this.shouldShowChallengeCard) {
        options.push({
          type: 'challenge',
          title: this.$t('common.challenge_one'),
          icon: 'mdi-target',
          description: this.$t('challenge.create_challenge_card_description'),
        })
      }

      return options
    },
  },

  methods: {
    handleTypeSelection(type) {
      this.selectedType = type
      this.currentStep = STEPS.FORM
      if (this.selectedType === 'task') {
        this.$emit('close')
        this.trackTasks('tasks.openCreateDrawer', { from: 'menu' })
        return eventHub.$emit(
          HUB_EVENTS.TASK_CREATION_DRAWER.CREATE,
          {
            isPrivate: false,
          },
          'createModal'
        )
      }
    },

    handleClose() {
      if (this.isLoading) return
      this.currentStep = STEPS.SELECTION
      this.$emit('close')
    },

    handleCancel() {
      if (this.replicateRevData) {
        this.$emit('close')
        return
      }
      this.currentStep = STEPS.SELECTION
    },
  },
}
</script>
